import React, { useMemo } from 'react';

import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { DeleteConfirm } from '@features/FilePreviewer/ui/DeleteConfirm';
import {
    ColorPicker,
    ExecutorsStaff,
    FilePreviewer,
    ModalDatePicker,
    ModalFilesUploader,
    MultiSelectTags,
    ObjectsOrEstimatesSelect,
    ObserversStaff,
    TaskAuthor,
    TaskStatusSwitcherBlock,
} from 'features';
import { CustomModal, ModalTitle, Popup, RadioBtn, UiButton } from 'shared/ui';
import { TextEditor } from 'shared/ui/TextEditor/TextEditor';

import cls from './ModalEditTask.module.css';
import { Loader } from '../../../../components/UI/NewLoader/Loader/Loader';
import { ModalContext } from '../../model/context';

import type { IModalAttachment } from '@entities/attachment/model/IModalAttachment.interface';
import type { FieldErrors } from 'entities/modal/model/types/modalFieldErrors';
import type { IAttachmentResponse, Task } from 'entities/task/model/types/tasks';
import type { TModalMode } from 'shared/model/types/modalMode.type';

type TModalData = {
    taskTitle?: string;
    taskDescription?: string;
    date: string;
};

interface ModalEditTaskViewProps {
    attachments: (IModalAttachment | IAttachmentResponse)[];
    deleteConfirmModal: boolean;
    deleteConfirmPopup: boolean;
    fieldErrors: FieldErrors;
    fileIdToDeleteFromList: string | null;
    fileIdToPreview: string | null;
    isCheckedRadio?: boolean;
    isModalLoading: boolean;
    isObserver: boolean;
    modalData: TModalData;
    modalMode: TModalMode | null;
    pickedTaskUid: string | null;
    portalFileOverlays?: boolean;
    previewEnable: boolean;
    smetterCssClassName?: string;
    taskData?: Task;
    commentsPanel?: React.ReactNode;
    triggerAnimationError: boolean;
    deleteAttachment: (fileId: string | undefined) => void;
    deleteTaskHandler: () => void;
    handleRadioBtn: () => void;
    onChangeEditModalTitle: (title: string) => void;
    onChangeModalDescription: (description: string) => void;
    onEnablePreviewModal: (enablePreview: boolean, fileId: string) => void;
    onHideModal: () => void;
    onHidePrevModal: () => void;
    onSubmit: () => Promise<void>;
    setAttachments: React.Dispatch<
        React.SetStateAction<(IModalAttachment | IAttachmentResponse)[]>
    >;
    setDeleteConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
    setFieldErrors: React.Dispatch<React.SetStateAction<FieldErrors>>;
    setFileIdToDeleteFromList: React.Dispatch<React.SetStateAction<string | null>>;
    setTriggerAnimationError: React.Dispatch<React.SetStateAction<boolean>>;
    switchDeleteConfirmPopup: () => void;
}

export const ModalEditTaskView = ({
    attachments,
    deleteConfirmModal,
    deleteConfirmPopup,
    fieldErrors,
    fileIdToDeleteFromList,
    fileIdToPreview,
    isCheckedRadio,
    isModalLoading,
    isObserver,
    modalData,
    modalMode,
    pickedTaskUid,
    portalFileOverlays = false,
    previewEnable,
    smetterCssClassName,
    taskData,
    commentsPanel,
    triggerAnimationError,
    deleteAttachment,
    deleteTaskHandler,
    handleRadioBtn,
    onChangeEditModalTitle,
    onChangeModalDescription,
    onEnablePreviewModal,
    onHideModal,
    onHidePrevModal,
    onSubmit,
    setAttachments,
    setDeleteConfirmModal,
    setFieldErrors,
    setFileIdToDeleteFromList,
    setTriggerAnimationError,
    switchDeleteConfirmPopup,
}: ModalEditTaskViewProps) => {
    const { t } = useTranslation();
    const modalContextValue = useMemo(
        () => ({
            attachments,
        }),
        [attachments]
    );
    const fileOverlays = (
        <>
            {deleteConfirmModal && (
                <DeleteConfirm
                    onHideModal={() => setDeleteConfirmModal(false)}
                    onDeleteFile={deleteAttachment}
                    fileId={fileIdToDeleteFromList}
                />
            )}
            <FilePreviewer
                filesToPreview={attachments}
                isOpen={previewEnable}
                onHide={onHidePrevModal}
                fileIdToPreview={fileIdToPreview}
                onDeleteFile={deleteAttachment}
                canDelete={!isObserver}
            />
        </>
    );

    return (
        <ModalContext.Provider value={modalContextValue}>
            <Popup
                isOpen={deleteConfirmPopup}
                contentText={t('ModalEditTask.DeleteConfirmPopup.text')}
                type="delete"
                deleteButtonText={t('ModalEditTask.DeleteConfirmPopup.dangerBtn')}
                cancelButtonText={t('ModalEditTask.DeleteConfirmPopup.cancelBtn')}
                btnActionCb={deleteTaskHandler}
                onHidePopup={switchDeleteConfirmPopup}
                popupContentClassName={cls.deleteConfirmPopup}
            />
            {portalFileOverlays ? createPortal(fileOverlays, document.body) : fileOverlays}
            <CustomModal
                isOpen={modalMode === 'editTask'}
                onHideModal={onHideModal}
                classes={smetterCssClassName}
                contentClasses={classNames(
                    cls.ModalContainerWidth,
                    commentsPanel && cls.WithComments
                )}
            >
                <CustomModal.Header classes={cls.ModalHeader}>
                    <div className={classNames(cls.ModalHeaderWrapper)}>
                        <div className={cls.TitleWrapper}>
                            {taskData?.context?.highest_role != 'observer' && (
                                <RadioBtn
                                    isChecked={isCheckedRadio}
                                    onClickHandler={handleRadioBtn}
                                    fill={
                                        modalData?.taskTitle?.length === 0 ? '#ADB0B5' : undefined
                                    }
                                />
                            )}
                            <ModalTitle
                                isError={fieldErrors.title.isError}
                                onChangeHandler={(e) => onChangeEditModalTitle(e.target.value)}
                                placeholder={t('TaskModal.ModalTitle.placeholder')}
                                classes={classNames([
                                    isCheckedRadio ? cls.checkedTaskTitle : cls.taskTitle,
                                    taskData?.context?.highest_role != 'author' &&
                                        cls.disabledTaskTitle,
                                ])}
                                value={modalData?.taskTitle}
                                triggerAnimationError={triggerAnimationError}
                                setTriggerAnimationError={setTriggerAnimationError}
                                disabled={taskData?.context?.highest_role != 'author'}
                            />
                        </div>
                        <div className={cls.ModalInfoWrapper}>
                            <TaskStatusSwitcherBlock
                                taskId={pickedTaskUid}
                                isDisabled={isCheckedRadio || isObserver}
                            />

                            <TaskAuthor taskId={pickedTaskUid} />
                        </div>
                    </div>
                </CustomModal.Header>
                {isModalLoading && <Loader variant="light" />}
                <div className={cls.ModalColumns}>
                    <div className={cls.TaskForm}>
                        <CustomModal.Body classes={cls.ModalBody}>
                            <div className={cls.Container}>
                                <TextEditor
                                    inputName="text-editor"
                                    value={modalData.taskDescription}
                                    onChange={onChangeModalDescription}
                                    placeholder={t('TaskModal.TextEditor.placeholder')}
                                    readOnly={isObserver || isModalLoading}
                                    maxLength={2000}
                                    isDisabled={isCheckedRadio || isObserver}
                                />
                                <ModalFilesUploader
                                    modalMode={modalMode}
                                    attachments={attachments}
                                    setAttachments={setAttachments}
                                    setFileIdToDeleteFromList={setFileIdToDeleteFromList}
                                    onEnablePreviewModal={onEnablePreviewModal}
                                    setDeleteConfirmModal={setDeleteConfirmModal}
                                    previewEnable={previewEnable}
                                    canDelete={!isObserver}
                                    canUpload={!isObserver}
                                    isDisabled={
                                        isCheckedRadio ||
                                        isModalLoading ||
                                        isObserver ||
                                        attachments.length >= 10
                                    }
                                    isTaskChecked={isCheckedRadio}
                                />
                            </div>
                            <div className={cls.Container}>
                                <div className={cls.SelectedTagsContainer}>
                                    <MultiSelectTags
                                        modalMode={modalMode}
                                        isDisabled={
                                            isCheckedRadio ||
                                            taskData?.context?.highest_role != 'author'
                                        }
                                        error={fieldErrors.tags}
                                        setFieldErrors={setFieldErrors}
                                    />
                                </div>
                                <ColorPicker
                                    isDisabled={
                                        isCheckedRadio ||
                                        taskData?.context?.highest_role != 'author'
                                    }
                                    modalMode={modalMode}
                                />
                                <div className={cls.DateContainer}>
                                    <ModalDatePicker
                                        initialDate={modalData.date}
                                        modalMode={modalMode}
                                        isDisabled={taskData?.context.highest_role != 'author'}
                                        isCheckedRadio={isCheckedRadio}
                                        isModalLoading={isModalLoading}
                                        fieldErrors={fieldErrors}
                                        setFieldErrors={setFieldErrors}
                                    />
                                </div>
                            </div>
                            <ObjectsOrEstimatesSelect
                                isDisabled={
                                    isCheckedRadio ||
                                    isModalLoading ||
                                    taskData?.context?.highest_role != 'author'
                                }
                                modalMode={modalMode}
                                error={fieldErrors.objectOrEstimate}
                                setFieldErrors={setFieldErrors}
                            />
                            <div className={cls.RolesWrapper}>
                                <ExecutorsStaff
                                    text={t('Tasks.ExecutorsStaff.title')}
                                    modalMode={modalMode}
                                    isDisabled={
                                        isCheckedRadio ||
                                        isModalLoading ||
                                        taskData?.context?.highest_role != 'author'
                                    }
                                    error={fieldErrors.executor}
                                    setFieldErrors={setFieldErrors}
                                    highestRole={taskData?.context?.highest_role}
                                />
                                <ObserversStaff
                                    text={t('Tasks.ObserversStaff.title')}
                                    modalMode={modalMode}
                                    isDisabled={
                                        isCheckedRadio ||
                                        isModalLoading ||
                                        taskData?.context?.highest_role != 'author'
                                    }
                                    highestRole={taskData?.context?.highest_role}
                                />
                            </div>
                        </CustomModal.Body>
                        <CustomModal.Footer>
                            {taskData?.context?.highest_role === 'author' && (
                                <UiButton
                                    btnText={t('ModalEditTask.DeleteTaskBtn.text')}
                                    btnColor="danger"
                                    onClick={switchDeleteConfirmPopup}
                                />
                            )}
                            {taskData?.context?.highest_role != 'observer' && (
                                <UiButton
                                    disabled={
                                        isModalLoading ||
                                        fieldErrors.date.isError ||
                                        fieldErrors.tags.isError ||
                                        fieldErrors.title.isError ||
                                        fieldErrors.description.isError ||
                                        fieldErrors.executor.isError ||
                                        fieldErrors.objectOrEstimate.isError
                                    }
                                    onClick={onSubmit}
                                    btnText={t('ModalEditTask.SaveTaskBtn.text')}
                                    className={cls.submitBtn}
                                />
                            )}
                            {isObserver && (
                                <UiButton
                                    onClick={onHideModal}
                                    disabled={isModalLoading}
                                    className={cls.submitBtn}
                                    btnText={t('ModalEditTask.CloseBtn.text')}
                                />
                            )}
                        </CustomModal.Footer>
                    </div>
                    {commentsPanel}
                </div>
            </CustomModal>
        </ModalContext.Provider>
    );
};
